import React from "react";
import { useNavigate } from "react-router-dom";
import { useDemoPage } from "@/lib/useDemoPage";
import { BRAND_NAME } from "@/config/brand";

// Page Family Offices et CGP. Le contenu ne décrit que ce que le produit fait aujourd'hui :
// vue consolidée, rapport PDF consolidé, export CSV, situations de propriété, accès limités
// et journalisés. Aucune certification ni intégration n'est promise ici.
const CAPABILITIES = [
  {
    title: "Vue consolidée multi-familles",
    text: "Familles, œuvres, niveaux de risque et suivi de conservation réunis dans un tableau de bord unique.",
  },
  {
    title: "Rapport PDF consolidé",
    text: "Un document prêt à présenter en comité patrimonial ou lors d'une revue d'assurance.",
  },
  {
    title: "Export CSV",
    text: "Une ligne par œuvre, toutes familles confondues, pour l'intégrer à vos propres outils de reporting.",
  },
  {
    title: "Situations de propriété",
    text: "Pleine propriété, usufruit et nue-propriété sont renseignés sur les fiches et remontent dans le suivi d'une famille.",
  },
  {
    title: "Accès limités et journalisés",
    text: "Chaque accès est accordé explicitement, avec une date d'expiration possible, révocable, et les consultations sont journalisées.",
  },
  {
    title: "Données hébergées en Europe",
    text: "La base de données est hébergée dans l'Union européenne (région de Paris).",
  },
];

const ROLES = [
  ["La famille", "Détient la collection et décide de ce qu'elle partage."],
  ["Le Family Office", "Consulte et consolide. Il ne remplace ni l'Art Advisor ni l'expert."],
  ["L'Art Advisor", "Reste responsable des données artistiques et du suivi de la collection."],
  ["L'expert", "Documente une valeur, sur une mission délimitée et avec une source."],
  ["Le notaire", "Qualifie la situation juridique. La décision reste hors plateforme."],
];

const PILOT_STEPS = [
  "Un pilote sur une ou deux familles, avec leurs données réelles autorisées.",
  "Un rapport consolidé produit à partir de ces données.",
  "Un export CSV testé dans le workflow de votre cabinet.",
  "Une revue des droits et des responsabilités avec votre équipe conformité.",
  "Un chiffrage qui sépare l'abonnement, l'import initial, la formation et les éventuelles intégrations.",
];

export default function FamilyOfficeDemo() {
  const navigate = useNavigate();
  const trackCta = useDemoPage("family-office");
  const go = (path) => {
    trackCta(path);
    navigate(path);
  };
  const pilotPath = "/contact?plan=family_office&enterprise=true";
  const demoPath = "/contact?plan=family_office&demo=true";

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-neutral-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="max-w-4xl">
            <div className="inline-block px-5 py-2 bg-neutral-800 text-neutral-300 text-xs font-bold tracking-widest rounded mb-8">
              FAMILY OFFICES ET CGP
            </div>
            <h1
              className="text-5xl md:text-6xl font-serif mb-8 tracking-tight leading-tight"
              style={{ fontFamily: "Georgia, serif" }}
            >
              Rendre l'actif art lisible dans la vision patrimoniale
            </h1>
            <p className="text-xl text-neutral-300 mb-10 leading-relaxed max-w-3xl">
              Consolidez l'actif art de plusieurs familles sans brouiller les responsabilités : {BRAND_NAME} organise
              l'information, chaque professionnel reste dans son rôle.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => go(pilotPath)}
                className="px-10 py-4 bg-white text-black rounded font-semibold hover:bg-neutral-100 transition-colors text-lg"
              >
                Cadrer un pilote
              </button>
              <button
                onClick={() => go(demoPath)}
                className="px-10 py-4 border-2 border-white text-white rounded font-semibold hover:bg-white hover:text-black transition-colors text-lg"
              >
                Demander une démonstration
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Ce que le produit fait aujourd'hui */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <h2 className="text-4xl font-serif mb-4" style={{ fontFamily: "Georgia, serif" }}>
            Ce que vous obtenez aujourd'hui
          </h2>
          <p className="text-xl text-neutral-500">Des fonctions en service, à valider avec vous sur un pilote.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CAPABILITIES.map((c) => (
            <div key={c.title} className="p-7 rounded-2xl border border-neutral-200 bg-white">
              <h3 className="text-xl font-semibold mb-2">{c.title}</h3>
              <p className="text-neutral-600 leading-relaxed">{c.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Rôles */}
      <div className="bg-neutral-50 py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-serif mb-3 text-center" style={{ fontFamily: "Georgia, serif" }}>
            Chacun dans son rôle
          </h2>
          <p className="text-lg text-neutral-500 text-center mb-10">
            Le Family Office ne devient pas Art Advisor. La plateforme ne devient pas expert ni notaire.
          </p>
          <dl className="divide-y divide-neutral-200 bg-white border border-neutral-200 rounded-2xl">
            {ROLES.map(([who, what]) => (
              <div key={who} className="px-6 py-4 grid sm:grid-cols-[180px_1fr] gap-2">
                <dt className="font-semibold">{who}</dt>
                <dd className="text-neutral-600">{what}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-6 text-sm text-neutral-500">
            Les valeurs affichées sont des estimations indicatives, distinguées des expertises signées. Elles ne sont ni
            juridiquement ni fiscalement opposables.
          </p>
        </div>
      </div>

      {/* Pilote */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <h2 className="text-4xl font-serif mb-3 text-center" style={{ fontFamily: "Georgia, serif" }}>
          Commencer par un pilote sur deux familles
        </h2>
        <p className="text-lg text-neutral-500 text-center mb-10">Pas d'engagement d'ensemble avant d'avoir vu un rapport réel.</p>
        <ol className="space-y-5">
          {PILOT_STEPS.map((s, i) => (
            <li key={s} className="flex items-start gap-5">
              <span className="flex-shrink-0 w-10 h-10 rounded-full bg-black text-white flex items-center justify-center font-bold">
                {i + 1}
              </span>
              <span className="pt-1.5 text-lg text-neutral-700">{s}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Confidentialité */}
      <div className="bg-black text-white py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-serif mb-6" style={{ fontFamily: "Georgia, serif" }}>
            Confidentialité et traçabilité
          </h2>
          <ul className="space-y-3 text-lg text-neutral-200">
            <li>Une collection reste privée tant que la famille ne partage rien.</li>
            <li>Un accès est limité aux œuvres et aux documents expressément partagés.</li>
            <li>Il peut expirer, être révoqué à tout moment, et chaque consultation est journalisée.</li>
            <li>Les données sont hébergées dans l'Union européenne.</li>
          </ul>
          <p className="mt-6 text-neutral-400">
            Nous détaillons ces points, et les limites de responsabilité de chacun, avec votre équipe conformité lors du
            cadrage du pilote.
          </p>
        </div>
      </div>

      {/* Tarif */}
      <div className="bg-neutral-50 py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-serif mb-8" style={{ fontFamily: "Georgia, serif" }}>
            Tarif
          </h2>
          <div className="bg-white border-2 border-black rounded-2xl p-10">
            <p className="text-sm text-neutral-500 mb-1">Abonnement Family Office</p>
            <p className="text-5xl font-bold mb-4">
              À partir de 299 €<span className="text-xl text-neutral-500"> / mois</span>
            </p>
            <p className="text-neutral-600 leading-relaxed">
              Le nombre de familles incluses, les comptes associés, l'import, le support et les éventuelles intégrations
              sont précisés au cadrage.
            </p>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Migration de données, formation et développements spécifiques restent des prestations séparées, sur devis.
            </p>
          </div>
        </div>
      </div>

      {/* CTA final */}
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 className="text-5xl font-serif mb-6" style={{ fontFamily: "Georgia, serif" }}>
          Cadrons un pilote
        </h2>
        <p className="text-xl text-neutral-500 mb-10 max-w-2xl mx-auto leading-relaxed">
          Chaque Family Office est différent. Parlons de vos familles, de votre reporting et de ce qui vous ferait dire
          que le pilote a réussi.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <button
            onClick={() => go(pilotPath)}
            className="px-12 py-5 bg-black text-white rounded font-bold hover:bg-neutral-800 transition-colors text-lg"
          >
            Cadrer un pilote
          </button>
          <button
            onClick={() => go(demoPath)}
            className="px-12 py-5 border-2 border-neutral-300 text-neutral-700 rounded font-semibold hover:border-black hover:text-black transition-colors text-lg"
          >
            Demander une démonstration
          </button>
        </div>
      </div>
    </div>
  );
}
